import React, { useCallback } from 'react'
import { Trade, Currency } from 'vvs-sdk'
import { InjectedModalProps } from 'vvs-uikit'
import { useTranslation } from 'contexts/Localization'
import TransactionConfirmationModal, {
  ConfirmationModalContent,
  TransactionErrorContent,
} from 'components/TransactionConfirmationModal'
import useGelatoLimitOrdersLib from 'state/limitOrders/useGelatoLimitOrdersLib'
import LimitOrdersModalFooter from './LimitOrdersModalFooter'
import LimitOrdersModalHeader from './LimitOrdersModalHeader'

interface ConfirmLimitOrdersModalProps {
  limitPrice: string
  currencies: {
    input: Currency
    output: Currency
  }
  formattedAmounts: {
    input: string
    output: string
    price: string
  }
  rawAmounts: {
    input: string | undefined
    output: string | undefined
  }
  trade?: Trade
  attemptingTxn: boolean
  txHash?: string
  onConfirm: () => void
  swapErrorMessage?: string
  customOnDismiss?: () => void
}

const ConfirmLimitOrdersModal: React.FC<InjectedModalProps & ConfirmLimitOrdersModalProps> = ({
  trade,
  limitPrice,
  currencies,
  formattedAmounts,
  rawAmounts,
  onConfirm,
  onDismiss,
  customOnDismiss,
  swapErrorMessage,
  attemptingTxn,
  txHash,
}) => {
  const { t } = useTranslation()
  const library = useGelatoLimitOrdersLib()
  const { minReturn, gelatoFee } = library.getFeeAndSlippageAdjustedMinReturn(rawAmounts.output)

  const modalHeader = useCallback(() => {
    return trade ? (
      <LimitOrdersModalHeader trade={trade} currencies={currencies} formattedAmounts={formattedAmounts} />
    ) : null
  }, [trade, currencies, formattedAmounts])

  const modalBottom = useCallback(() => {
    return trade ? (
      <LimitOrdersModalFooter
        onConfirm={onConfirm}
        limitPrice={limitPrice}
        trade={trade}
        currencies={currencies}
        minReturn={minReturn}
        gelatoFee={gelatoFee}
        swapErrorMessage={swapErrorMessage}
      />
    ) : null
  }, [onConfirm, swapErrorMessage, trade, currencies, gelatoFee, minReturn, limitPrice])

  // text to show while loading
  const pendingText = t('Placing your limit order')

  const confirmationContent = useCallback(
    () =>
      swapErrorMessage ? (
        <TransactionErrorContent onDismiss={onDismiss} message={swapErrorMessage} />
      ) : (
        <ConfirmationModalContent topContent={modalHeader} bottomContent={modalBottom} />
      ),
    [onDismiss, modalBottom, modalHeader, swapErrorMessage],
  )

  return (
    <TransactionConfirmationModal
      title={t('Confirm Limit Order')}
      onDismiss={onDismiss}
      customOnDismiss={customOnDismiss}
      attemptingTxn={attemptingTxn}
      hash={txHash}
      content={confirmationContent}
      pendingText={pendingText}
    />
  )
}

export default ConfirmLimitOrdersModal
